import { type WalletClient, type TransactionRequest, encodeFunctionData, parseUnits } from "viem";
import { gatewayV1Abi } from "@/abi";
import { TOKEN_CONFIG, getGatewayContractAddress } from "@/config";
import { estimateContractTransactionGas, writeContractTransaction } from "@/utils";
import { type TransactionHash, TOKEN_IDS } from "@/types";

/**
 * Prepares an unstake and lock transaction
 */
export const prepareUnstakeAndLockTransaction = async (
    amount: number,
    unwindingEpochs: number,
    walletClient: WalletClient,
): Promise<TransactionRequest> => {
    if (!walletClient.account) {
        throw new Error("Wallet account is not available");
    }

    if (!walletClient.chain?.id) {
        throw new Error("Chain ID is not available");
    }

    const amountBigInt = parseUnits(`${amount}`, TOKEN_CONFIG[TOKEN_IDS.SIUSD].decimals);
    const contractAddress = getGatewayContractAddress(walletClient.chain.id);

    return {
        to: contractAddress,
        data: encodeFunctionData({
            abi: gatewayV1Abi,
            functionName: "unstakeAndLock",
            args: [walletClient.account.address, amountBigInt, unwindingEpochs],
        }),
    };
};

/**
 * Estimates gas for an unstake and lock transaction
 */
export const estimateUnstakeAndLockTransactionGas = async (
    amount: number,
    unwindingEpochs: number,
    walletClient: WalletClient,
): Promise<bigint> => {
    if (!walletClient) {
        throw new Error("Wallet account is not available");
    }

    const txRequest = await prepareUnstakeAndLockTransaction(amount, unwindingEpochs, walletClient);
    return estimateContractTransactionGas(walletClient, txRequest);
};

export const gatewayUnstakeAndLockTransaction = async (
    amount: number,
    unwindingEpochs: number,
    walletClient: WalletClient,
): Promise<TransactionHash> => {
    const txRequest = await prepareUnstakeAndLockTransaction(amount, unwindingEpochs, walletClient);
    const hash = await writeContractTransaction(walletClient, txRequest);
    return hash as TransactionHash;
};
